export interface BaseMachine {
  model: string;
  manufacturer?: {
    name: string;
  };
  customBrandName?: string;
  productionYear?: number;
  type?: {
    name: string;
  };
  axisConfiguration?: {
    name: string;
  };
  operationType?: {
    name: string;
  };
  automationLevel?: {
    name: string;
  };
}

export const getMachineDisplayName = (machine: BaseMachine): string => {
  const brandName = machine?.customBrandName || machine?.manufacturer?.name || "";
  return brandName + " " + machine?.model;
};

export const getProductionYearDisplay = (machine: BaseMachine): string => {
  return machine?.productionYear && machine?.productionYear > 0 ? machine?.productionYear.toString() : "";
};

export const getBrandDisplayName = (machine: BaseMachine): string => {
  return machine?.customBrandName || machine?.manufacturer?.name || "";
};

/**
 * Formats manufacturer name with parent-child hierarchy using chevron breadcrumb.
 * e.g. "Tajmac-ZPS > Manurhin" when parent exists, just "DMG Mori" when no parent.
 */
export const formatManufacturerWithHierarchy = (
  name: string,
  parentName: string | null | undefined
): string => {
  if (!parentName) return name;
  return `${parentName} > ${name}`;
};

export const getMachineName = (machine?: BaseMachine | null): string => {
  if (!machine || !machine.type?.name) return "";

  switch (machine.type.name) {
    case "CNC Machine":
      const axisConfig = machine.axisConfiguration?.name;
      const operationType = machine.operationType?.name;

      const cncParts = [];
      if (axisConfig) { cncParts.push(axisConfig); }
      cncParts.push("CNC");
      if (operationType) { cncParts.push(operationType); }

      // Don't append "Machine" if operationType already ends with Machine/Center/Centre.
      // Special case: "Machining" → use "Machining Center" instead of "Machining Machine".
      const opLower = operationType?.toLowerCase() || "";
      if (opLower === "machining") {
        // Replace last element ("Machining") with "Machining Center"
        cncParts[cncParts.length - 1] = "Machining Center";
      } else {
        const alreadyHasSuffix = opLower.endsWith("machine") || opLower.endsWith("center") || opLower.endsWith("centre");
        if (!alreadyHasSuffix) {
          cncParts.push("Machine");
        }
      }

      return cncParts.join(" ");

    case "Production line":
      const automationLevel = machine.automationLevel?.name;
      const lineOperationType = machine.operationType?.name;

      const lineParts = [];
      if (automationLevel) lineParts.push(automationLevel);
      if (lineOperationType) lineParts.push(lineOperationType);

      if (automationLevel || lineOperationType) {
        lineParts.push("Line");
      }

      return lineParts.join(" ");

    default:
      return machine.type.name + " Machine";
  }
}; 